import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	AccountStatementFilter,
	getAccountStatementPeriodsApi,
	getAccountStatementYearsApi,
} from "api/accounting";
import { getBuyersFilterApi } from "api/common";
import CustomButton from "ui/Button";
import CustomDropDownList from "ui/dropDown";
import { ICON_NAMES } from "utils/constant";
import { useUserStore } from "store/index";
import { userProfileBuyersApi } from "api/user";
import { Buyer } from "interface/buyer";

interface Props {
	filterOptions: AccountStatementFilter;
	setFilterOptions: (filter: AccountStatementFilter) => void;
	periods: number[];
	setPeriods: (periods: number[]) => void;
	exportAsExcel: () => void;
	exportAsPdf: () => void;
}

const Filter: FC<Props> = ({
	filterOptions,
	setFilterOptions,
	periods,
	setPeriods,
	exportAsExcel,
	exportAsPdf,
}) => {
	const { t } = useTranslation();
	const [years, setYears] = useState<number[]>([]);
	const { isAdmin, isCompanyAdmin } = useUserStore();
	const [buyers, setBuyers] = useState<Buyer[]>([]);
	const calledRef = useRef(false);

	useEffect(() => {
		if (calledRef.current && filterOptions.buyerLedgerAccount !== 0) {
			return;
		}
		const data: any = {
			id: filterOptions.buyerLedgerAccount,
			name: filterOptions.buyerName,
			year: filterOptions.year,
		};

		if (data.id && Number(data.id) !== 0) {
			calledRef.current = true;
		}
		handleBuyer(data)
			.then(() => {
				// if (filterOptions.year) {
				// 	handleYear({ value: filterOptions.year });
				// }
			})
			.catch((error) => {
				console.error("Error handling buyer:", error);
			});

		if (isAdmin || isCompanyAdmin) {
			return;
		}
		getBuyers("")
			.then((buyers) => {
				setBuyers(buyers);
			})
			.catch((error) => {
				console.error("Error fetching buyers:", error);
			});
	}, [filterOptions.buyerLedgerAccount]);

	const handleBuyer = async (buyer: any) => {
		let buyerLedgerAccount = 0;
		if (!buyer || !buyer.id || Number(buyer.id) === 0) {
			setFilterOptions({
				...filterOptions,
				buyerLedgerAccount: 0,
				buyerName: "",
			});
			setYears([]);

			setPeriods([]);
			return;
		}
		buyerLedgerAccount = Number(buyer.id);
		const buyerName = buyer.name || buyer.displayName || "";
		const response = await getAccountStatementYearsApi(buyerLedgerAccount);
		const availableYears = [...(response.data || [])].sort((a, b) => b - a);
		const year = availableYears[0] ?? filterOptions.year;
		setYears(availableYears);

		const periodsResponse = await getAccountStatementPeriodsApi(
			buyerLedgerAccount,
			year,
		);
		const availablePeriods = Array.from(
			new Set(periodsResponse.data || []),
		).sort((a, b) => a - b);
		setPeriods(availablePeriods);

		setFilterOptions({
			...filterOptions,
			buyerLedgerAccount,
			buyerName,
			year,
			monthFrom: availablePeriods[0],
			monthTo: availablePeriods[availablePeriods.length - 1],
		});
	};

	const handleYear = (item: { value: number }) => {
		const year = item.value;
		setFilterOptions({
			...filterOptions,
			year,
		});
	};
	const getBuyers = async (text: string) => {
		if (isAdmin) {
			const response = await getBuyersFilterApi(text);
			return response || [];
		}
		//  else if (isCompanyAdmin) {
		// 	const response = await getUserSettingBuyersFilterApi();

		// 	return response || [];
		// }
		const response = await userProfileBuyersApi();
		const buyers = response.data || [];
		const data: any[] = buyers.map((buyer) => ({
			id: buyer.id,
			name: buyer.description,
			orgNo: buyer.businessRegisterNumber,
			isInFdir: "",
			status: "", //buyer.status,
			displayName: buyer.description,
		}));
		return data;
	};

	return (
		<div className="filter-container">
			<div className="filter-items">
				<div style={{ width: 250 }}>
					<CustomDropDownList
						data={buyers}
						value={{
							id: Number(filterOptions.buyerLedgerAccount) || null,
							name: filterOptions.buyerName || " ",
						}}
						dataItemKey="id"
						textField="name"
						label={t("Buyer")}
						defaultItem={{ id: null, name: t("None") }}
						filterable={isAdmin}
						serverFilter={true}
						serverFilterApi={getBuyers}
						onSelect={handleBuyer}
					/>
				</div>

				<div style={{ width: 170 }}>
					<CustomDropDownList
						label={t("Year")}
						data={years.map((year) => ({ value: year, text: String(year) }))}
						value={{
							value: filterOptions.year,
							text: String(filterOptions.year),
						}}
						dataItemKey="value"
						textField="text"
						disabled={!filterOptions.buyerLedgerAccount || years.length === 0}
						serverFilter={false}
						onSelect={handleYear}
					/>
				</div>

				<div style={{ width: 170 }}>
					<CustomDropDownList
						label={t("PeriodFrom")}
						data={periods.map((period) => ({
							value: period,
							text: period,
						}))}
						value={{
							value: filterOptions.monthFrom || 0,
							text: filterOptions.monthFrom || " ",
						}}
						dataItemKey="value"
						textField="text"
						disabled={periods.length === 0}
						serverFilter={false}
						onSelect={(item) =>
							setFilterOptions({
								...filterOptions,
								monthFrom: item.value,
							})
						}
					/>
				</div>
				<div style={{ width: 170 }}>
					<CustomDropDownList
						label={t("PeriodTo")}
						data={periods.map((period) => ({
							value: period,
							text: String(period),
						}))}
						value={{
							value: filterOptions.monthTo || 0,
							text: String(filterOptions.monthTo || " "),
						}}
						dataItemKey="value"
						textField="text"
						disabled={periods.length === 0}
						serverFilter={false}
						onSelect={(item) =>
							setFilterOptions({
								...filterOptions,
								monthTo: item.value,
							})
						}
					/>
				</div>
			</div>

			<div className="filter-end-btn">
				<CustomButton
					label="PDF"
					onClick={exportAsPdf}
					iconName={ICON_NAMES.Download}
				/>
				<CustomButton
					label="Excel"
					onClick={exportAsExcel}
					iconName={ICON_NAMES.Download}
				/>
			</div>
		</div>
	);
};

export default Filter;
